import React from 'react';
import { User } from 'lucide-react';

interface MentionTextProps {
  text: string;
  className?: string;
}

export const MentionText: React.FC<MentionTextProps> = ({ text, className = '' }) => {
  // Regex to match mentions: @username, @email, or @"display name"
  const mentionRegex = /@([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|[a-zA-Z0-9._-]+|"[^"]+")(?=\s|$|[^\w@])/g;
  
  const parseTextWithMentions = (inputText: string) => {
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = mentionRegex.exec(inputText)) !== null) {
      const beforeMatch = inputText.slice(lastIndex, match.index);
      if (beforeMatch) {
        parts.push({
          type: 'text',
          content: beforeMatch,
          key: `text-${lastIndex}`
        });
      }
      
      // Extract mention without @ symbol
      let mentionContent = match[1];
      
      // Remove quotes if present
      if (mentionContent.startsWith('"') && mentionContent.endsWith('"')) {
        mentionContent = mentionContent.slice(1, -1);
      }
      
      parts.push({
        type: 'mention',
        content: mentionContent,
        key: `mention-${match.index}`
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after last mention
    const remainingText = inputText.slice(lastIndex);
    if (remainingText) {
      parts.push({
        type: 'text',
        content: remainingText,
        key: `text-${lastIndex}`
      });
    }
    
    return parts;
  };

  const renderMentionBadge = (content: string, key: string) => {
    // Check if it's an email
    const isEmail = content.includes('@');
    const displayText = isEmail ? content.split('@')[0] : content;
    
    return (
      <span
        key={key}
        className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 rounded-full text-xs font-semibold border border-blue-200 hover:from-blue-200 hover:to-blue-100 hover:border-blue-300 hover:shadow-sm transition-all duration-200 cursor-default mx-0.5"
        title={isEmail ? `Email: ${content}` : `User: @${content}`}
      >
        <User className="h-3 w-3 text-blue-600" />
        <span>{displayText}</span>
        {isEmail && (
          <span className="text-blue-500 opacity-70 text-[10px] ml-0.5">@</span>
        )}
      </span>
    );
  };

  const parts = parseTextWithMentions(text);
  
  if (parts.length === 0) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {parts.map((part) => {
        if (part.type === 'mention') {
          return renderMentionBadge(part.content, part.key);
        }
        return <span key={part.key}>{part.content}</span>;
      })}
    </span>
  );
};